import { NavLink, Link } from "react-router-dom";
import {
  LayoutGrid, FileText, BarChart3, Zap, BookOpen, Settings as SettingsIcon, LifeBuoy, LogOut,
} from "lucide-react";
import { NeptuneLogo } from "../../../components/neptune/NeptuneMark";
import { useMerchantAuth } from "../../../context/MerchantAuthContext";
import { useMerchantApplication } from "../../../context/MerchantApplicationContext";

/**
 * Sidebar — Brief 2 §1 + §2.
 * - Top: company name (falls back to "Your account" until step 1 fills it).
 * - Body: 5 nav items + Support which opens the Nerea modal (no route).
 * - Bottom: user email + logout button.
 */
/** First letter of the user's email for the sidebar avatar.
 * Defensive against:
 *  - empty / missing email (renders "?"),
 *  - emails starting with non-letters ("+test@...", "123@...") — picks first
 *    alphabetic character if any, otherwise falls back to the first char.
 *  - very long emails (the parent truncates the visible email; this is just one char). */
function avatarLetter(email) {
  if (!email || typeof email !== "string") return "?";
  const firstAlpha = email.match(/[A-Za-z]/);
  if (firstAlpha) return firstAlpha[0].toUpperCase();
  const firstChar = email.trim()[0];
  return firstChar ? firstChar.toUpperCase() : "?";
}

export default function Sidebar({ onClose, onOpenSupport }) {
  const { user, logout } = useMerchantAuth();
  const { application } = useMerchantApplication();
  const company = application?.company_name || "Your account";

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 pt-6 pb-2">
        <Link to="/" data-testid="app-sidebar-logo" className="inline-flex items-center">
          <NeptuneLogo height={22} />
        </Link>
      </div>

      <div className="px-6 mt-7">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-600">— Workspace</div>
        <div
          data-testid="app-sidebar-company"
          className="mt-2 font-display text-[18px] font-semibold tracking-tight text-white truncate"
          title={company}
        >
          {company}
        </div>
      </div>

      <nav className="mt-7 px-3 flex-1 overflow-y-auto" aria-label="App navigation">
        <NavItem to="/app/dashboard" icon={LayoutGrid} label="Dashboard" testId="app-nav-dashboard" onClose={onClose} />
        <NavItem to="/app/onboarding" icon={FileText} label="Onboarding" testId="app-nav-onboarding" onClose={onClose} />
        <NavItem to="/app/financials" icon={BarChart3} label="Financials" testId="app-nav-financials" onClose={onClose} />
        <NavItem to="/app/gateway" icon={Zap} label="Gateway" testId="app-nav-gateway" onClose={onClose} />
        {(application?.status === "approved" || application?.status === "live") && (
          <NavItem to="/app/integration" icon={BookOpen} label="Integration" testId="app-nav-integration" onClose={onClose} />
        )}
        <NavItem to="/app/settings" icon={SettingsIcon} label="Settings" testId="app-nav-settings" onClose={onClose} />

        <button
          type="button"
          onClick={() => { onClose?.(); onOpenSupport(); }}
          data-testid="app-nav-support"
          className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] text-zinc-400 hover:text-white hover:bg-white/[0.03] transition-colors"
        >
          <LifeBuoy className="h-3.5 w-3.5" /> Support
        </button>
      </nav>

      <div className="mt-4 px-4 pb-5">
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#0055FF] flex items-center justify-center text-[11px] font-medium text-white shrink-0">
            {avatarLetter(user?.email)}
          </div>
          <div className="min-w-0 flex-1">
            <div data-testid="app-sidebar-email" className="text-[12.5px] text-white truncate" title={user?.email}>{user?.email || "—"}</div>
            <div className="text-[10.5px] text-zinc-500 tracking-[0.16em] uppercase">Account</div>
          </div>
          <button
            onClick={logout}
            data-testid="app-sidebar-logout"
            title="Log out"
            aria-label="Log out"
            className="h-7 w-7 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-colors shrink-0"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, testId, onClose }) {
  return (
    <NavLink
      to={to}
      onClick={() => onClose?.()}
      data-testid={testId}
      end={to === "/app/dashboard"}
      className={({ isActive }) =>
        `relative flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
          isActive
            ? "bg-white/[0.05] text-white"
            : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[2px] rounded-r bg-gradient-to-b from-[#00E5FF] to-[#0055FF]" />
          )}
          <Icon className="h-3.5 w-3.5" />
          {label}
        </>
      )}
    </NavLink>
  );
}
